import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import { data, useNavigate } from "react-router-dom";
import GradientButton from "../components/common/GradiantButton";
import { investorApi } from "../api/api";
import Loader from "../components/common/Loader";


const BookedPropertiesScreen = () => {
  const {isAuthenticated, userId, user_refferal_code } = useSelector((state) => state.user);
  const [properties, setProperties] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
 
  const navigate = useNavigate();
  const theme = useSelector((data) => data.themeValue.colors);
  const themeMode = useSelector((data) => data.themeValue.theme);
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const fetchProperties = async () => {
      try {
        const response = await investorApi.getBookedProperties(userId);
        if (response.status === 200) {
          setProperties(response.data);
        }
      } catch (error) {
        console.error("API Error:", error);
      }
      finally {
        setLoading(false);
      }
    };
 
    if (userId) {
      fetchProperties();
    }
  }, [userId]);

  const generateSlug = (property) => {
        const titlePart = `${property.name}-${property.city}`
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-")
            .trim();

        return `${titlePart}-${property.id}`;
    };

    const handleCardClick = (property) => {
        const slug = generateSlug(property);

        let basePath = "";

        if (property.type === "property") {
            basePath = `/property/${slug}`;
        } else if (property.type === "project") {
            basePath = `/project/${slug}`;
        } else if (property.type === "villa") {
            basePath = `/villa/${slug}`;
        } else if (property.type === "apartment") {
            basePath = `/apartment/${slug}`;
        }

        if (isAuthenticated && user_refferal_code) {
            basePath += `?ref=${user_refferal_code}`;
        }

        window.open(basePath, "_blank");
    };
 
  // const handleCardClick = (property) => {
  //   const path = property.type === 'property'
  //     ? `/PropertyDetailsScreen?${property.refferal_code}/${user_refferal_code}`
  //     : property.type === 'project'
  //       ? `/BookedProjectDetailsScreen?${property.refferal_code}/${user_refferal_code}`
  //       : property.type === 'villa'
  //         ? `/IndividualVillaScreen?${property.refferal_code}/${user_refferal_code}`
  //         : property.type === 'apartment'
  //           ? `/ApartmentPlotsScreen?${property.refferal_code}/${user_refferal_code}` : " ";
 
  //   navigate(path, {
  //     state: {
  //       propertyId: property.refferal_code,
  //       projectId: property.refferal_code,
  //       villaId: property.refferal_code,
  //       type: property.type,
  //       data:property
  //     },
  //   });
  // };
 
  if (loading) {
    return (
      <Loader />
    )
  }
 
  const filteredProperties = properties.filter((property) => {
    const matchesType = typeFilter === "all" || property.type.toLowerCase() === typeFilter;
    return matchesType;
  });
 
  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
      {filteredProperties.length > 0 ? (
        <>
          {/* Type Filter */}
          <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1, mb: 3 }}>
            {["all", "property", "project", "villa", "apartment"].map((type) => (
              <Button
                key={type}
                variant={typeFilter === type ? "contained" : "outlined"}
                onClick={() => setTypeFilter(type)}
                sx={{ textTransform: "capitalize" }}
              >
                {type}
              </Button>
            ))}
          </Box>
 
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: 4,
              mt: 4,
            }}
          >
            {filteredProperties.map((property, id) => (
              <Card
                key={id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  overflow: "hidden",
                  cursor: "pointer",
                  backgroundColor: theme.background,
                  boxShadow:
                    themeMode === "light"
                      ? "0 4px 16px rgba(0, 0, 0, 0.2)"
                      : "0 4px 16px rgba(255, 255, 255, 0.1)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 6px 24px rgba(0, 0, 0, 0.12)",
                  },
                }}
                onClick={() => handleCardClick(property)}
              >
                {/* Image and Type Badge */}
                <Box position="relative">
                  <CardMedia
                    component="img"
                    height="180"
                    image={property.iconUrl || "/placeholder.jpg"}
                    alt={property.name}
                    sx={{ objectFit: "cover" }}
                  />
                  <Box
                    sx={{
                      backgroundColor: '#ffffffcc',
                      color: '#333',
                      position: 'absolute',
                      bottom: '17px',
                      right: '10px',
                      padding: '5px 12px',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textTransform: 'capitalize',
                      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {property.type}
                  </Box>
                </Box>
 
                {/* Content */}
                <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6" fontWeight={600} sx={{ color: theme.text }} gutterBottom>
                    {property.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.text }}>
                    <strong>City:</strong> {property.city}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.text }}>
                    <strong>Address:</strong> {property.address}
                  </Typography>
                  {property.Numberofplots !== undefined && (
                    <Typography variant="body2" sx={{ color: theme.text }}>
                      <strong>Plots:</strong> {property.Numberofplots}
                    </Typography>
                  )}
                  <Typography>
                    <strong>Payment Status: </strong>
                    <span
                      style={{
                        color:
                          property.payment_status === "Pending"
                            ? "#1E90FF" // DodgerBlue
                            : property.payment_status === "InProgress"
                              ? "#FFA500" // Orange
                              : property.payment_status === "Approved"
                                ? "#28a745" // Green
                                : property.payment_status === "Rejected"
                                  ? "#dc3545" // Red
                                  : "#000000",
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: "12px",
                        backgroundColor:
                          property.payment_status === "Pending"
                            ? "#e6f0ff"
                            : property.payment_status === "InProgress"
                              ? "#fff4e6"
                              : property.payment_status === "Approved"
                                ? "#e6ffed"
                                : property.payment_status === "Rejected"
                                  ? "#ffe6e6"
                                  : "#f0f0f0",
                        fontSize: "0.85rem",
                      }}
                    >
                      {property.payment_status}
                    </span>
                  </Typography>
 
 
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 1,
                      mt: "auto",
                      pt: 2,
                    }}
                  >
                    <GradientButton
                      text="View Details"
                      onClick={() => handleCardClick(property)}
                      width="100%"
                      height={45}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            bgcolor: theme.background,
            mt: -8, // optional tweak to vertically center
          }}
        >
          <Typography variant="h4" fontWeight={600} gutterBottom color={theme.primary_text_color}>
            No Booked Properties
          </Typography>
          <Typography variant="body1" color={theme.secondary_text_color} mb={4}>
            You haven't booked any properties yet. <br />
            Start browsing to find properties!
          </Typography>
          <GradientButton
            text="Browse Properties"
            onClick={() => navigate("/PropertyList")}
            width={{ xs: "70%", sm: "40%", md: "40%", lg: "20%" }}
          />
        </Box>
      )}
    </Box>
 
  );
 
};
 
export default BookedPropertiesScreen;